import React from 'react';
import Icon from '../../../components/AppIcon';
import { motion } from 'framer-motion';

const SolutionOverviewSection = () => {
  const steps = [
    {
      id: 1,
      number: "1",
      icon: "Home",
      title: "Ontdek wat bij uw woning past",
      description: "Type huis, budget en persoonlijke wensen",
      color: "bg-primary/10 text-primary"
    },
    {
      id: 2,
      number: "2",
      icon: "Search",
      title: "Vergelijk oplossingen & aanbieders",
      description: "Op inhoud, reviews en ervaringen",
      color: "bg-secondary/10 text-secondary"
    },
    {
      id: 3,
      number: "3",
      icon: "BookOpen",
      title: "Lees onafhankelijke uitleg & tips",
      description: "Objectieve informatie zonder verkooppraat",
      color: "bg-success/10 text-success"
    },
    {
      id: 4,
      number: "4",
      icon: "Handshake",
      title: "Kom in contact met betrouwbare leverancier",
      description: "Geverifieerde professionals in uw regio",
      color: "bg-accent/10 text-accent-foreground"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    hover: {
      rotate: [0, -10, 10, -10, 0],
      scale: [1, 1.1, 1.1, 1.1, 1],
      transition: {
        duration: 0.5
      }
    }
  };

  const numberVariants = {
    hover: {
      scale: 1.2,
      backgroundColor: "var(--primary)",
      color: "var(--primary-foreground)",
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background relative overflow-hidden">
      {/* Animated background elements */}
      <motion.div
        className="absolute top-0 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl"
        animate={{
          y: [0, 50, 0],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-0 right-1/4 w-64 h-64 bg-secondary/5 rounded-full blur-3xl"
        animate={{
          y: [0, -50, 0],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-8 sm:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Zo helpt ons platform u
          </h2>
          <p className="text-lg font-body text-muted-foreground max-w-3xl mx-auto">
            Een helder proces dat u begeleidt van oriëntatie tot realisatie
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {steps?.map((step, index) => (
            <motion.div
              key={step?.id}
              variants={stepVariants}
              whileHover="hover"
              className="relative"
            >
              {/* Connector Line (desktop only) */}
              {index < steps?.length - 1 && (
                <motion.div 
                  className="hidden lg:block absolute top-16 left-1/2 w-full h-0.5 bg-border z-0"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                >
                  <motion.div 
                    className="absolute right-0 top-1/2 transform -translate-y-1/2"
                    animate={{
                      x: [0, 5, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </motion.div>
                </motion.div>
              )}

              {/* Step Card */}
              <div className="relative z-10 text-center bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.25)] transform perspective-1000 transition-all" style={{ transformStyle: 'preserve-3d' }}>
                <motion.div 
                  className={`w-20 h-20 ${step?.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15)] hover:shadow-[0_15px_40px_-8px_rgba(0,0,0,0.25)] transition-all transform perspective-1000`}
                  variants={iconVariants}
                  whileInView={{ 
                    scale: [0, 1.2, 1],
                    rotate: [0, 360, 360]
                  }}
                  viewport={{ once: true }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.15,
                    ease: "easeOut"
                  }}
                  whileHover={{
                    y: -12,
                    scale: 1.15,
                    boxShadow: "0 15px 40px -8px rgba(0, 0, 0, 0.25), 0 0 20px rgba(59, 130, 246, 0.15)",
                    rotateY: 10,
                    transition: { duration: 0.3 }
                  }}
                  style={{ transformStyle: 'preserve-3d', transform: 'translateZ(30px)' }}
                >
                  <Icon name={step?.icon} size={32} />
                </motion.div>
                <div className="mb-2" style={{ transform: 'translateZ(15px)' }}>
                  <motion.span 
                    className="inline-block w-8 h-8 bg-foreground text-background rounded-full text-sm font-heading font-bold flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.15)]"
                    variants={numberVariants}
                    whileHover={{ 
                      scale: 1.3,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.25)"
                    }}
                  >
                    {step?.number}
                  </motion.span>
                </div>
                <motion.h3 
                  className="text-lg font-heading font-semibold text-foreground mb-2"
                  whileHover={{ scale: 1.05, color: "var(--primary)" }}
                  transition={{ duration: 0.2 }}
                  style={{ transform: 'translateZ(10px)' }}
                >
                  {step?.title}
                </motion.h3>
                <p className="text-sm font-body text-muted-foreground" style={{ transform: 'translateZ(5px)' }}>
                  {step?.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-base font-body text-muted-foreground italic">
            Geen pusherige verkoop, alleen begeleiding naar de juiste keuze voor úw situatie
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionOverviewSection;